import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import Draggable from 'react-draggable';
import { Portal } from 'react-portal';
import { useAutoAnimate } from '@formkit/auto-animate/react';

import { CalendarItemModel } from 'shared/models';
import { useAsyncEffect, useMedia, useNavigate, useObservable } from 'shared/hooks';
import { UiButton, UiIcon, UiLoading, UiModal, UiModalContent, UiUserBadge } from 'shared/uikit-new';
import { calendarItemsDeleteQuery, calendarItemsGetQuery } from 'shared/queries';
import { CalendarItemTypeEnum } from 'shared/enums';
import { COLORS, ROUTES } from 'shared/constants';
import { notifier } from 'shared/utilities';

import { Store } from 'pages/calendar/store';

import './styles.scss';

interface PropsType {
    calendarItem: CalendarItemModel;
    isOpened: boolean;
    onClose: () => void;
    bounds?: DOMRect | null;
}

export const CalendarPageCalendarItemPreview = observer(function CalendarPageCalendarItemPreview({ isOpened, onClose, bounds, calendarItem }: PropsType) {
    const navigate = useNavigate();
    const ref = useRef<HTMLDivElement>(null);
    const [refInner] = useAutoAnimate();
    const store = useObservable({
        isLoading: true,
        calendarItem: new CalendarItemModel(calendarItem),
        left: 0,
        top: 0,
        zIndex: 0,
    });

    const { isDesktop } = useMedia();

    // useOnClickOutside(ref, onClose);

    useEffect(() => {
        store.set('zIndex', +`${Math.floor(Date.now() / 1000)}`.slice(-4));
    }, [store, isOpened]);

    useEffect(() => {
        if (!bounds) {
            return;
        }
        const width = 500;
        const height = ref.current?.offsetHeight ? ref.current?.offsetHeight : 350;
        let x = bounds.x + bounds.width + 4;
        if (window.innerWidth - x < 504) {
            x = bounds.x - 4 - width;

            if (x < 0) {
                x = window.innerWidth - 532;
            }
        }
        const fromTop = bounds.y;
        store.set('left', x);
        store.set('top', window.innerHeight - fromTop > height ? fromTop : window.innerHeight - height - 30);
        store.set('zIndex', +`${Math.floor(Date.now() / 1000)}`.slice(-4));

        if (!store.isLoading) {
            const height = ref.current?.offsetHeight ? ref.current?.offsetHeight : 350;
            store.set('top', window.innerHeight - fromTop > height ? fromTop : window.innerHeight - height - 30);
        }
    }, [store, bounds, store.isLoading]);

    useAsyncEffect(async () => {
        if (!isOpened) {
            return;
        }
        store.set('isLoading', true);
        const response = await calendarItemsGetQuery({ id: calendarItem.id, changeKey: calendarItem.changeKey });
        if (response.isSuccess && response.data) {
            store.set('calendarItem', new CalendarItemModel(response.data.item));
        }
        store.set('isLoading', false);
    }, [isOpened]);

    if (!isOpened) {
        return null;
    }

    const handleEdit = () => {
        Store.edit.update({
            isOpened: true,
            calendarItem: new CalendarItemModel(store.calendarItem),
        });

        onClose();
    };

    const handleDelete = async () => {
        const isConfirmed = await notifier().confirm({
            title: 'Удалить событие?',
            description: 'Вы действительно хотите удалить это событие?',
            actions: resolve => [
                <UiButton
                    key='cancel'
                    onClick={() => resolve(false)}
                    style={{
                        backgroundColor: [COLORS._F3F5F8, COLORS._C6CAD1],
                        color: [COLORS._2C2F35, COLORS._2C2F35],
                    }}
                >
                    Отменить
                </UiButton>,
                <UiButton
                    key='accept'
                    onClick={() => resolve(true)}
                    style={{
                        backgroundColor: [COLORS._E84118, COLORS._CF3A15],
                        color: [COLORS._FFFFFF, COLORS._FFFFFF],
                    }}
                >
                    <UiIcon.Trash2 size={20} />
                    <span>Удалить</span>
                </UiButton>,
            ],
        });

        if (!isConfirmed) {
            return;
        }

        calendarItemsDeleteQuery({
            id: store.calendarItem.id,
            changeKey: store.calendarItem.changeKey,
        });

        Store.set(
            'calendarItems',
            Store.calendarItems.filter(item => item.id !== store.calendarItem.id)
        );
    };

    const content = () => {
        if (store.isLoading) {
            return <UiLoading />;
        }

        return (
            <>
                <div className='calendar-page-calendar-item-preview__header'>
                    <div className='calendar-page-calendar-item-preview__mark' style={{ backgroundColor: store.calendarItem.color }}></div>
                    <div className={'calendar-page-calendar-item-preview__subject'}>{store.calendarItem.subject}</div>
                </div>
                <div className='calendar-page-calendar-item-preview__values'>
                    <div className='calendar-page-calendar-item-preview__value'>
                        <UiIcon.Clock8 size={24} />
                        <span>{store.calendarItem.startAtDate.format('dddd, DD MMMM; HH:mm') + '-' + calendarItem.endAtDate.format('HH:mm')}</span>
                    </div>
                    {!!store.calendarItem.educationTypeId && (
                        <div className='calendar-page-calendar-item-preview__value'>
                            <UiIcon.GraduationCap size={24} />
                            <span>{store.calendarItem.educationType.name}</span>
                        </div>
                    )}
                    {!!store.calendarItem.body && (
                        <div className='calendar-page-calendar-item-preview__value calendar-page-calendar-item-preview__value--description'>
                            <UiIcon.AlignLeft size={24} />
                            <span>{store.calendarItem.body}</span>
                        </div>
                    )}
                    {store.calendarItem.attendees.length > 0 && (
                        <div className='calendar-page-calendar-item-preview__value'>
                            <UiIcon.UsersRound size={24} />
                            <span>
                                {store.calendarItem.attendees.map(user => (
                                    <UiUserBadge key={user.email} user={user} />
                                ))}
                            </span>
                        </div>
                    )}
                </div>
                <div className='calendar-page-calendar-item-preview__actions'>
                    {!calendarItem.type.is(CalendarItemTypeEnum.Task) && (
                        <>
                            <UiButton
                                onClick={handleDelete}
                                template={'link'}
                                style={{ color: [COLORS._E84118, COLORS._E84118], backgroundColor: [COLORS.TRANSPARENT, COLORS.TRANSPARENT] }}
                            >
                                <UiIcon.Trash2 size={20} />
                                <span>Удалить</span>
                            </UiButton>
                            <UiButton
                                onClick={handleEdit}
                                template={'link'}
                                style={{ color: [COLORS._9EA2AA, COLORS._9EA2AA], backgroundColor: [COLORS.TRANSPARENT, COLORS.TRANSPARENT] }}
                            >
                                <UiIcon.PencilLine size={20} />
                                <span>Редактировать</span>
                            </UiButton>
                        </>
                    )}
                    {calendarItem.type.is(CalendarItemTypeEnum.Task) && (
                        <UiButton
                            template={'link'}
                            style={{ color: [COLORS._9EA2AA, COLORS._9EA2AA], backgroundColor: [COLORS.TRANSPARENT, COLORS.TRANSPARENT] }}
                            onClick={() => {
                                navigate(ROUTES.TASK_TRACKER(calendarItem.taskTrackerTask?.spaceId) + `?previewTaskId=${calendarItem.taskTrackerTask?.id}`);
                            }}
                        >
                            <UiIcon.Eye size={20} />
                            <span>Перейти</span>
                        </UiButton>
                    )}
                </div>
            </>
        );
    };

    if (!isDesktop) {
        return (
            <UiModal isOpened={isOpened} onClose={onClose}>
                <UiModalContent>
                    {content()}
                </UiModalContent>
            </UiModal>
        );
    }

    return (
        <Portal>
            <Draggable
                nodeRef={ref}
                cancel={'.calendar-page-calendar-item-preview__values, .calendar-page-calendar-item-preview__actions, .calendar-page-calendar-item-preview__close'}
            >
                <div className={'calendar-page-calendar-item-preview'} style={{ left: store.left, top: store.top, zIndex: store.zIndex }} ref={ref}>
                    <div className='calendar-page-calendar-item-preview__close'>
                        <UiIcon.X size={24} onClick={onClose} />
                    </div>
                    <div className='calendar-page-calendar-item-preview__inner' ref={refInner}>
                        {content()}
                    </div>
                </div>
            </Draggable>
        </Portal>
    );
});
